export const ACCESS_TOKEN_COOKIE = 'auth:app';
export const SESSION_HINT_KEY = 'sporto_session_hint';

export const getCookieDomain = (): string | undefined => {
  const domain = import.meta.env.VITE_COOKIE_DOMAIN;
  return domain && domain !== 'localhost' ? domain : undefined;
};

export const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

export const setCookie = (name: string, value: string, expires?: string) => {
  const domain = getCookieDomain();
  const domainStr = domain ? `; domain=${domain}` : '';
  document.cookie = `${name}=${encodeURIComponent(value)}${expires ? `; expires=${expires}` : ''}${domainStr}; path=/; SameSite=Lax`;
};

export const removeCookie = (name: string) => {
  const domain = getCookieDomain();
  const domainStr = domain ? `; domain=${domain}` : '';
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC${domainStr}; path=/;`;
};

export const setSessionHint = () => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(SESSION_HINT_KEY, '1');
  }
};

export const clearSessionHint = () => {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(SESSION_HINT_KEY);
  }
};

export const getAccessToken = (): string | null => {
  const token =
    getCookie(ACCESS_TOKEN_COOKIE) ||
    getCookie('auth_community_token') ||
    getCookie('auth:client');
  if (token) return token;
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('token');
    if (ssoToken) return ssoToken;
  }
  return null;
};

export const hasSessionHint = (): boolean => {
  if (
    getCookie(ACCESS_TOKEN_COOKIE) ||
    getCookie('auth_community_token') ||
    getCookie('auth:client')
  ) {
    return true;
  }
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('token')) return true;
  }
  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem(SESSION_HINT_KEY) === '1';
  }
  return false;
};

export const AUTH_COOKIE_NAMES = {
  APP: 'auth:app',
  MITRA: 'auth:ownerstaffs',
  MITRA_REFRESH: 'auth:ownerstaffs_refresh',
  ADMIN: 'auth:admin',
  ADMIN_REFRESH: 'auth:admin_refresh',
  CLIENT: 'auth:client',
  CLIENT_REFRESH: 'auth:client_refresh',
  COMMUNITY: 'auth_community_token',
  COMMUNITY_REFRESH: 'auth_community_refresh_token',
  SUPERADMIN: 'auth_superadmin_token',
  SUPERADMIN_REFRESH: 'auth_superadmin_refresh_token',
};

export const setAccessTokenCookie = (token: string, expUnixSeconds: number): void => {
  const expires = new Date(expUnixSeconds * 1000).toUTCString();
  setCookie(ACCESS_TOKEN_COOKIE, token, expires);
};

export const clearAccessTokenCookie = (): void => {
  removeCookie(ACCESS_TOKEN_COOKIE);
};

export const clearSession = (): void => {
  clearAccessTokenCookie();
  clearSessionHint();
};

export const COMMUNITY_UNVERIFIED_KEY = 'sporto_community_unverified';
export const LOGOUT_FLAG_KEY = 'sporto_just_logged_out';

export const setLogoutFlag = (): void => {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(LOGOUT_FLAG_KEY, '1');
  }
};

export const hasLogoutFlag = (): boolean => {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(LOGOUT_FLAG_KEY) === '1';
};

export const clearLogoutFlag = (): void => {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(LOGOUT_FLAG_KEY);
  }
};

export const setCommunityUnverifiedFlag = (value: boolean): void => {
  if (typeof sessionStorage !== 'undefined') {
    if (value) {
      sessionStorage.setItem(COMMUNITY_UNVERIFIED_KEY, '1');
    } else {
      sessionStorage.removeItem(COMMUNITY_UNVERIFIED_KEY);
    }
  }
};

export const hasCommunityUnverifiedFlag = (): boolean => {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(COMMUNITY_UNVERIFIED_KEY) === '1';
};
